import { z } from "zod";

// Teams sharing: an item's access list and the arrays derived from it. Spec:
// the "Teams Sharing Rules" doc, section "Permission function".

export const AccessEntryLevelEnum = z.enum(["view", "edit"]);
export type AccessEntryLevel = z.infer<typeof AccessEntryLevelEnum>;

export const AccessLevelEnum = z.enum(["none", "view", "edit"]);
export type AccessLevel = z.infer<typeof AccessLevelEnum>;

export const TEAM_KEY_PREFIX = "team:";
export const teamReaderKey = (firmId: string) => `${TEAM_KEY_PREFIX}${firmId}`;

// A member entry is a uid. The team key only ever comes from the Everyone entry.
export const MemberIdSchema = z
  .string()
  .min(1)
  .refine((k) => !k.startsWith(TEAM_KEY_PREFIX), "not a member id");

// Empty (everyone none, no members) = Private.
export const AccessListSchema = z.object({
  everyone: AccessLevelEnum.optional(),
  members: z.record(MemberIdSchema, AccessEntryLevelEnum).optional(),
});
export type AccessList = z.infer<typeof AccessListSchema>;

// A Share-dialog save. Only the entries it names change, so two people saving
// at once cannot undo each other's changes. Unknown keys (e.g. a whole
// `members` list) and a uid both set and removed are refused.
export const GrantDiffSchema = z
  .object({
    everyone: AccessLevelEnum.optional(),
    set: z.record(MemberIdSchema, AccessEntryLevelEnum).optional(),
    remove: z.array(z.string()).optional(),
  })
  .strict()
  .refine(
    (d) =>
      !(d.remove ?? []).some(
        (uid) => d.set && Object.prototype.hasOwnProperty.call(d.set, uid),
      ),
    "a uid is both set and removed",
  );
export type GrantDiff = z.infer<typeof GrantDiffSchema>;

// Absent on documents that predate the model; readers treat them as owner-only.
// readers/editors come from deriveArrays: the backend writes them, except that
// a client-side create stamps [owner] (the create rule requires it).
export const AccessFieldsSchema = z.object({
  created_by: z.string().optional(),
  access: AccessListSchema.optional(),
  readers: z.array(z.string()).optional(),
  editors: z.array(z.string()).optional(),
});
export type AccessFields = z.infer<typeof AccessFieldsSchema>;

export const EMPTY_ACCESS: AccessList = { everyone: "none", members: {} };

// Anything but "view" or "edit" counts as none.
export const levelOf = (v: unknown): AccessLevel =>
  v === "view" || v === "edit" ? v : "none";

// A stored list with invalid keys and levels dropped.
export function normalizeAccess(raw: unknown): AccessList {
  const list = (raw && typeof raw === "object" ? raw : {}) as {
    everyone?: unknown;
    members?: unknown;
  };
  const members: Record<string, AccessEntryLevel> = {};
  if (list.members && typeof list.members === "object") {
    for (const [uid, value] of Object.entries(list.members)) {
      const level = levelOf(value);
      if (level !== "none" && MemberIdSchema.safeParse(uid).success) {
        members[uid] = level;
      }
    }
  }
  return { everyone: levelOf(list.everyone), members };
}

export type ResourcePerm = "read" | "read_write" | "no_access";

export type TeamListFilter = "all" | "created_by_me" | "shared_with_me";

// `teams` = firms listed in the user's permissions.teamsAdmin/teamsDesigner
// whose `teams` row is not inactive, as useWorkspaceContext and the backend's
// isUserMemberOfTeam resolve it (the arrays alone keep inactive members).
// `readOnlyTeams` = teams whose subscription has lapsed: their items are
// read-only for everyone, owners included.
export interface AccessUser {
  id: string;
  teams: readonly string[];
  readOnlyTeams?: readonly string[];
}

// A normalized item, built from a Firestore doc by toAccessResource. `owner`
// holds the owner-only rights; `created_by` never changes.
export interface AccessResource {
  owner: string;
  created_by?: string;
  team: string | null;
  access?: AccessList;
  publicLink?: boolean;
  project?: AccessResource | null;
}
